import type { Theme } from "@mui/material/styles";
import { match, P } from "ts-pattern";

import type {
  BaseFormTemplate,
  FormContextData,
  FormContextMiniForm,
} from "@thalamos/aspire-common";
import { getLatestVersion } from "@thalamos/aspire-common";

import { buildRedoStatusTooltip } from "./redoRequest.js";
import type { WorkItemStatusSummary } from "~/components/design-system/index.js";
import { calculateFormSignatureStatus } from "~/components/design-system/index.js";

type AdmissionWorkItem = Pick<
  FormContextData["workItems"][number],
  "formId" | "requestType" | "status"
>;

/**
 * The Summary pill for one form row of an admission. A status describing what is
 * being done to the form outranks its signature count, which is the fallback.
 */
export function admissionFormSummary({
  theme,
  form,
  template,
  workItems,
  isMedRec,
  hasDirectRedoRequest,
}: {
  theme: Theme;
  form: FormContextMiniForm;
  template: Pick<BaseFormTemplate, "parts">;
  workItems: AdmissionWorkItem[];
  isMedRec: boolean;
  hasDirectRedoRequest: boolean;
}): WorkItemStatusSummary {
  // Redo (non-s15) status, driven by the form's own redo state.
  const redoSummary: WorkItemStatusSummary | undefined = match(form.redoState)
    .with("void", () => ({
      statusText: "Void",
      colour: theme.palette.grey[600],
    }))
    .with("redo-requested", () => ({
      statusText: "Redo requested",
      colour: theme.palette.statuses.warning,
      tooltip: buildRedoStatusTooltip(
        isMedRec
          ? { type: "med-rec-redo-requested" }
          : hasDirectRedoRequest
            ? { type: "application-redo-requested-direct" }
            : { type: "application-redo-requested-inherited" },
      ),
    }))
    .with("redo-in-progress", () => ({
      statusText: "Redo in progress",
      colour: theme.palette.statuses.info,
      textColour: theme.palette.common.white,
      tooltip: buildRedoStatusTooltip(
        isMedRec
          ? { type: "med-rec-redo-in-progress" }
          : { type: "application-redo-in-progress" },
      ),
    }))
    .with(P.nullish, () => undefined)
    .exhaustive();
  if (redoSummary) {
    return redoSummary;
  }

  const amendWorkItem = workItems.find(
    (w) => w.formId === form.id && w.requestType === "amend",
  );
  // An accepted amend is complete once the form's latest version is; until
  // then the amend is still being made.
  const amendSummary: WorkItemStatusSummary | undefined = match(
    amendWorkItem?.status,
  )
    .with("accepted", () =>
      getLatestVersion(form).status === "complete"
        ? {
            statusText: "Amend completed",
            colour: theme.palette.statuses.positive,
          }
        : {
            statusText: "Amend in progress",
            colour: theme.palette.statuses.pending,
          },
    )
    .with("pending", () => ({
      statusText: "Amend requested",
      colour: theme.palette.statuses.pending,
    }))
    .with(
      "rejected",
      "cancelled",
      "merged",
      "completed",
      P.nullish,
      () => undefined,
    )
    .exhaustive();
  if (amendSummary) {
    return amendSummary;
  }

  const deMinimisWorkItem = workItems.find(
    (w) => w.formId === form.id && w.requestType === "de-minimis",
  );
  const deMinimisSummary: WorkItemStatusSummary | undefined = match(
    deMinimisWorkItem?.status,
  )
    .with("pending", () => ({
      statusText: "De minimis requested",
      colour: theme.palette.statuses.pending,
    }))
    .with("accepted", () => ({
      statusText: "De minimis in progress",
      colour: theme.palette.statuses.pending,
    }))
    .with(
      "rejected",
      "cancelled",
      "merged",
      "completed",
      P.nullish,
      () => undefined,
    )
    .exhaustive();
  if (deMinimisSummary) {
    return deMinimisSummary;
  }

  return calculateFormSignatureStatus(theme, form, template);
}
